import React, { useState } from 'react';
import { Box, CircularProgress, Paper, Typography } from '@mui/material';
import { SEARCE_COLORS } from '../../theme';
import SectionTitle from './SectionTitle';
import type { Goal, GoalStatus } from './executiveSummaryTypes';

const GOAL_STATUS: Record<
  GoalStatus,
  { label: string; bg: string; color: string }
> = {
  'in-progress': { label: 'In progress', bg: '#fff7ed', color: '#c2410c' },
  'on-track': { label: 'On track', bg: '#ecfdf5', color: '#15803d' },
  'at-risk': { label: 'At risk', bg: '#fef2f2', color: '#b91c1c' },
  done: { label: 'Done', bg: '#eff6ff', color: SEARCE_COLORS.primary },
};

interface OutcomesAndGoalsSectionProps {
  goals: Goal[];
  /** When true, shows a compact loading state inside the card */
  loading?: boolean;
  /** When true, omit outer Paper (e.g. nested inside ProjectHeroCard) */
  embedded?: boolean;
}

const OutcomesAndGoalsSection: React.FC<OutcomesAndGoalsSectionProps> = ({
  goals,
  loading,
  embedded = false,
}) => {
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const toggleExpanded = (id: string) => {
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const inner = (
    <>
      <SectionTitle>Outcomes &amp; Goals</SectionTitle>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={32} sx={{ color: SEARCE_COLORS.primary }} />
        </Box>
      ) : (
        <Box
          component="table"
          sx={{
            width: '100%',
            borderCollapse: 'separate',
            borderSpacing: 0,
            border: `1px solid ${SEARCE_COLORS.border}`,
            borderRadius: '6px',
            overflow: 'hidden',
            fontSize: '0.8125rem',
            tableLayout: 'fixed',
          }}
        >
          <colgroup>
            <col style={{ width: 'auto' }} />
            <col style={{ width: '8rem' }} />
          </colgroup>
          <Box
            component="thead"
            sx={{
              '& th': {
                background: `linear-gradient(135deg, ${SEARCE_COLORS.primary} 0%, ${SEARCE_COLORS.primary_light} 100%)`,
                color: '#fff',
                fontSize: '0.6875rem',
                fontWeight: 700,
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
                p: '8px 10px',
                textAlign: 'left',
              },
            }}
          >
            <tr>
              <th>Goal</th>
              <th>Status</th>
            </tr>
          </Box>
          <Box
            component="tbody"
            sx={{
              '& tr:nth-of-type(even) td': { background: '#fafbfc' },
              '& tr:last-of-type td': { borderBottom: 'none' },
              '& td': {
                p: '9px 10px',
                borderBottom: '1px solid #f1f5f9',
                verticalAlign: 'top',
                lineHeight: 1.45,
              },
            }}
          >
            {goals.length === 0 ? (
              <tr>
                <Box
                  component="td"
                  colSpan={2}
                  sx={{ borderBottom: 'none !important' }}
                >
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                    No business outcomes recorded for this project.
                  </Typography>
                </Box>
              </tr>
            ) : (
              goals.map((goal) => {
                const base = GOAL_STATUS[goal.status];
                const label = goal.statusLabel?.trim() || base.label;
                const isOpen = !!expanded[goal.id];
                return (
                  <tr key={goal.id}>
                    <td>
                      <Box
                        component="button"
                        type="button"
                        title={goal.description || undefined}
                        aria-expanded={isOpen}
                        onClick={() => toggleExpanded(goal.id)}
                        sx={{
                          width: '100%',
                          p: 0,
                          border: 'none',
                          background: 'none',
                          font: 'inherit',
                          color: 'inherit',
                          textAlign: 'left',
                          cursor: 'pointer',
                          borderRadius: '4px',
                          '&:hover': { background: 'rgba(0, 100, 255, 0.05)' },
                        }}
                      >
                        <Box
                          sx={
                            isOpen
                              ? { display: 'block', lineHeight: 1.45 }
                              : {
                                  display: '-webkit-box',
                                  WebkitBoxOrient: 'vertical',
                                  WebkitLineClamp: 2,
                                  overflow: 'hidden',
                                  lineHeight: 1.45,
                                }
                          }
                        >
                          {goal.text}
                        </Box>
                      </Box>
                    </td>
                    <td>
                      <Box
                        sx={{
                          display: 'inline-flex',
                          px: '10px',
                          py: '3px',
                          borderRadius: '999px',
                          fontSize: '0.6875rem',
                          fontWeight: 700,
                          whiteSpace: 'nowrap',
                          background: base.bg,
                          color: base.color,
                        }}
                      >
                        {label}
                      </Box>
                    </td>
                  </tr>
                );
              })
            )}
          </Box>
        </Box>
      )}
    </>
  );

  if (embedded) {
    return <Box sx={{ display: 'flex', flexDirection: 'column' }}>{inner}</Box>;
  }

  return (
    <Paper
      elevation={0}
      sx={{
        backgroundColor: '#fff',
        border: `1px solid ${SEARCE_COLORS.border}`,
        borderRadius: '10px',
        p: '20px 24px',
        boxShadow: '0 1px 4px rgba(0,0,0,.06), 0 0 0 1px rgba(0,0,0,.03)',
        mb: '20px',
      }}
    >
      {inner}
    </Paper>
  );
};

export default OutcomesAndGoalsSection;
